import { trans } from "@/_providers/Translation"

const JOBS_PER_TECHNICIAN = 100

export function calculateBaseline(historicalData) {
  if (!historicalData.length) return 0
  return historicalData.reduce((acc, v) => acc + (v || 0), 0) / historicalData.length
}

export function runScenario({ historicalData, scenarioType, changePercentage, forecastMonths }) {
  const baseline = calculateBaseline(historicalData)
  const pct = changePercentage / 100

  let forecastValues = []
  let forecastRevenue = null
  let requiredTechnicians = null

  if (scenarioType === "demand") {
    const forecastValue = baseline * (1 + pct)
    forecastValues = Array.from({ length: forecastMonths }, () => forecastValue)
  } else if (scenarioType === "price") {
    // Demand elasticity: +10% price → -5% demand (elasticity coefficient = -0.5)
    const demandMultiplier = 1 - pct * 0.5
    const forecastOrders = baseline * demandMultiplier
    forecastRevenue = forecastOrders * (1 + pct)
    forecastValues = Array.from({ length: forecastMonths }, () => forecastOrders)
  } else if (scenarioType === "resource") {
    const forecastJobs = baseline * (1 + pct)
    requiredTechnicians = Math.max(1, Math.ceil(forecastJobs / JOBS_PER_TECHNICIAN))
    forecastValues = Array.from({ length: forecastMonths }, () => forecastJobs)
  }

  return { baseline, forecastValues, forecastRevenue, requiredTechnicians }
}

export function buildRecommendation({ scenarioType, changePercentage, baseline, forecastValue, forecastRevenue, requiredTechnicians }) {
  const absPct = Math.abs(changePercentage)

  if (scenarioType === "demand") {
    if (changePercentage > 0) {
      const currentTechs = Math.ceil(baseline / JOBS_PER_TECHNICIAN)
      const forecastTechs = Math.ceil(forecastValue / JOBS_PER_TECHNICIAN)
      const extraTechs = forecastTechs - currentTechs
      if (extraTechs > 0) {
        return trans("analytics.recoDemandExtraTechs", { pct: absPct, count: extraTechs })
      }
      return trans("analytics.recoDemandIncrease", {
        pct: absPct,
        value: Math.round(forecastValue),
        baseline: Math.round(baseline),
      })
    }
    if (changePercentage < 0) {
      return trans("analytics.recoDemandDecrease", {
        pct: absPct,
        value: Math.round(forecastValue),
        baseline: Math.round(baseline),
      })
    }
    return trans("analytics.recoNoChange", { baseline: Math.round(baseline) })
  }

  if (scenarioType === "price") {
    if (changePercentage > 0) {
      return trans("analytics.recoPriceIncrease", {
        pct: absPct,
        revenue: Math.round(forecastRevenue ?? 0),
      })
    }
    if (changePercentage < 0) {
      return trans("analytics.recoPriceDecrease", {
        pct: absPct,
        revenue: Math.round(forecastRevenue ?? 0),
      })
    }
    return trans("analytics.recoNoPriceChange", { baseline: Math.round(baseline) })
  }

  if (scenarioType === "resource") {
    const direction = changePercentage >= 0
      ? trans("analytics.recoDirectionIncrease")
      : trans("analytics.recoDirectionDecrease")
    return trans("analytics.recoResourceChange", {
      pct: absPct,
      direction,
      count: requiredTechnicians,
      value: Math.round(forecastValue),
    })
  }

  return ""
}
